import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  getBrowserLanguage,
  getLanguage,
  isSupportedLanguage,
  LANGUAGE_SETUP_VERSION,
  LANGUAGE_STORAGE_KEY,
  SUPPORTED_LANGUAGES,
} from './languages';
import { observeLocalizedDom, translateSourceText } from './translationEngine';

const getStoredLanguage = () => {
  if (typeof window === 'undefined') return 'en';
  const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return isSupportedLanguage(stored) ? stored : getBrowserLanguage();
};

const defaultValue = {
  language: 'en',
  locale: 'en-US',
  direction: 'ltr',
  languages: SUPPORTED_LANGUAGES,
  setupRequired: false,
  preferencesReady: true,
  savingLanguage: false,
  t: (source) => source,
  previewLanguage: async () => {},
  changeLanguage: async () => ({ persisted: false }),
  completeLanguageSetup: async () => ({ persisted: false }),
};

const LanguageContext = createContext(defaultValue);

const getSetupStorageKey = (uid) => `bandz-language-setup-v${LANGUAGE_SETUP_VERSION}:${uid}`;

export const LanguageProvider = ({ currentUser, children }) => {
  const [language, setLanguage] = useState(getStoredLanguage);
  const [preferencesReady, setPreferencesReady] = useState(!currentUser);
  const [setupRequired, setSetupRequired] = useState(false);
  const [savingLanguage, setSavingLanguage] = useState(false);

  const applyLanguage = useCallback((nextLanguage) => {
    const supported = isSupportedLanguage(nextLanguage) ? nextLanguage : 'en';
    setLanguage(supported);
    if (typeof window !== 'undefined') window.localStorage.setItem(LANGUAGE_STORAGE_KEY, supported);
    return supported;
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (!currentUser) {
      setPreferencesReady(true);
      setSetupRequired(false);
      return () => { cancelled = true; };
    }

    setPreferencesReady(false);
    const loadPreferences = async () => {
      const localSetupComplete = window.localStorage.getItem(getSetupStorageKey(currentUser.uid)) === 'true';
      try {
        const preferencesRef = doc(db, 'users', currentUser.uid, 'preferences', 'settings');
        const snapshot = await getDoc(preferencesRef);
        const preferences = snapshot.exists() ? snapshot.data() : {};
        if (cancelled) return;

        if (isSupportedLanguage(preferences.language)) applyLanguage(preferences.language);
        const remoteSetupComplete = Number(preferences.languageSetupVersion || 0) >= LANGUAGE_SETUP_VERSION;
        setSetupRequired(!remoteSetupComplete && !localSetupComplete);

        if (!remoteSetupComplete && localSetupComplete) {
          void setDoc(preferencesRef, {
            language: isSupportedLanguage(preferences.language) ? preferences.language : getStoredLanguage(),
            languageSetupVersion: LANGUAGE_SETUP_VERSION,
            languageUpdatedAt: new Date().toISOString(),
          }, { merge: true });
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Could not load language preferences:', error);
          setSetupRequired(!localSetupComplete);
        }
      } finally {
        if (!cancelled) setPreferencesReady(true);
      }
    };

    void loadPreferences();
    return () => { cancelled = true; };
  }, [applyLanguage, currentUser]);

  useLayoutEffect(() => {
    const selected = getLanguage(language);
    document.documentElement.lang = selected.locale;
    document.documentElement.dir = selected.dir;
    return observeLocalizedDom(language);
  }, [language]);

  const persistLanguage = useCallback(async (nextLanguage, completesSetup) => {
    const supported = applyLanguage(nextLanguage);
    if (completesSetup && currentUser) {
      window.localStorage.setItem(getSetupStorageKey(currentUser.uid), 'true');
      setSetupRequired(false);
    }
    if (!currentUser) return { persisted: false };

    setSavingLanguage(true);
    try {
      const preferencesRef = doc(db, 'users', currentUser.uid, 'preferences', 'settings');
      await setDoc(preferencesRef, {
        language: supported,
        ...(completesSetup ? { languageSetupVersion: LANGUAGE_SETUP_VERSION } : {}),
        languageUpdatedAt: new Date().toISOString(),
      }, { merge: true });
      return { persisted: true };
    } catch (error) {
      console.error('Could not save language preference:', error);
      return { persisted: false, error };
    } finally {
      setSavingLanguage(false);
    }
  }, [applyLanguage, currentUser]);

  const previewLanguage = useCallback(async (nextLanguage) => {
    applyLanguage(nextLanguage);
  }, [applyLanguage]);

  const changeLanguage = useCallback(
    (nextLanguage) => persistLanguage(nextLanguage, true),
    [persistLanguage]
  );

  const selected = getLanguage(language);
  const value = useMemo(() => ({
    language,
    locale: selected.locale,
    direction: selected.dir,
    languages: SUPPORTED_LANGUAGES,
    setupRequired,
    preferencesReady,
    savingLanguage,
    t: (source) => translateSourceText(language, source),
    previewLanguage,
    changeLanguage,
    completeLanguageSetup: changeLanguage,
  }), [changeLanguage, language, preferencesReady, previewLanguage, savingLanguage, selected.dir, selected.locale, setupRequired]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => useContext(LanguageContext);

export default LanguageContext;
